import { Box, type Theme } from '@mui/material';
import type { ReactNode } from 'react';
import { alpha } from '@mui/material/styles';
import { glassSurface, tintedGlassSurface } from 'themes/componentOverrides';

export type TIconSurfaceVariant =
  'glass' | 'glassTinted' | 'glassStatus' | 'solid' | 'flat';

export type TIconSurfaceProps = {
  /**
   * `solid` is Figma's raised disc — the paper surface with a single soft
   * offset shadow (the "Round Button" behind a product mark). `flat` is the
   * same surface with no shadow, which is what the "Plan Benefits Card" rows
   * use.
   *
   * `glass`, `glassTinted` and `glassStatus` are the Liquid Glass disc,
   * reusing the exact recipe (and the exact opacities, already tuned and
   * visually verified — see componentOverrides.tsx) the plan-comparison
   * cards and toggle use, so an icon chip reads as the same material rather
   * than a fourth, separately hand-tuned glass surface. `glass` is the
   * neutral weight, for an icon on a photo or busy backdrop, or on an
   * unselected plan card. `glassTinted` is the "active glass" weight — a
   * translucent product-colour fill instead of neutral — for the same
   * "this one's selected" states the plan cards and toggle already use it
   * for (the featured/Prime plan's icon). `glassStatus` is the same
   * "active glass" weight again, but tinted by `tintColor` instead of
   * always the product colour — the popup result badge (StatusIcon) needs
   * its error state tinted with the secondary accent, not primary.
   */
  variant: TIconSurfaceVariant;
  /**
   * Required for `glassStatus`, ignored otherwise: the colour its
   * translucent fill, border and glow tint toward. `glassTinted` stays
   * hardcoded to `primary.main` — this is the one case (so far) where
   * "this is the one" isn't the product colour.
   */
  tintColor?: string;
  /** Diameter of the disc in px. Default 50, per Figma. */
  size?: number;
  onClick?: () => void;
  children: ReactNode;
};

/**
 * The paint half of `IconSurface`'s `glass` / `glassTinted` variants —
 * background, border, shadow (inset highlight + floating lift + a faint
 * product-colour glow) and the top-down sheen `::before`. Pulled out as its
 * own function, not just inlined in `IconSurface`, so a component that needs
 * this exact glass chip but ISN'T built on `<Icon>` — an `<IconButton>`, an
 * `<Avatar>` — can apply it directly via `sx` instead of re-deriving the
 * recipe a third time (see `TopBar.tsx`'s hamburger button and avatar).
 *
 * Layout (size, `border-radius`, `position: relative` for the `::before` to
 * anchor to) is the caller's job — this only returns paint.
 */
export const iconGlassChip = (
  theme: Theme,
  variant: 'glass' | 'glassTinted' | 'glassStatus',
  /** Required for `glassStatus`; ignored (always `primary.main`) otherwise. */
  tintColor?: string
) => {
  const dark = theme.palette.mode === 'dark';
  const floatingShadow = `0 6px 16px ${alpha('#000000', dark ? 0.45 : 0.2)}`;

  const paint = (() => {
    if (variant === 'glassTinted' || variant === 'glassStatus') {
      // `glassTinted` always tints toward the product colour; `glassStatus`
      // tints toward whatever `tintColor` the caller passes (the status
      // badge's success/error colour, not necessarily the product one).
      const tint =
        variant === 'glassStatus'
          ? (tintColor ?? theme.palette.primary.main)
          : theme.palette.primary.main;
      // glassStatus sits alone as a big, singular result badge rather than
      // one selected item among several siblings (glassTinted's case), so
      // it can afford — and reads better with — a much stronger tint than
      // the 0.22 that's right for a subtler "this one's selected" cue, to
      // read as close to the filled primary button's fully-opaque fill as
      // glass allows. The SAME 0.65 weight looked right in dark mode but
      // washed-out pale in light — the popup's own backdrop is pale in
      // light mode and near-black in dark, so identical alpha composites
      // very differently depending on what's behind it: 0.65 already lets
      // enough of a near-black backdrop through to read as deeply
      // saturated, but the same 35% of a pale backdrop reads as diluted.
      // Light needs to sit much higher to land in the same place.
      const weight =
        variant === 'glassStatus'
          ? { light: 0.85, dark: 0.65 }
          : { light: 0.22, dark: 0.22 };
      const surface = tintedGlassSurface(theme, weight, tint);
      // A stronger, standalone "floating disc" shadow — see the `glass`
      // branch below for why this needs to be more present than
      // glassSurface/tintedGlassSurface's own `shadows.card` layer at this
      // small a size.
      return {
        ...surface,
        // tintedGlassSurface's own border (1px, up to 0.65 alpha) is tuned
        // for card-sized surfaces, where it reads as a crisp edge on a big
        // area — at a ~40px disc the same ring reads as a hard, visible
        // outline rather than a soft glass edge (reported live: "the
        // border is quite visible"). A thinner, fainter hairline here,
        // matching the hairline weight `glassSurface` already uses.
        border: `0.5px solid ${alpha(tint, dark ? 0.45 : 0.4)}`,
        boxShadow: `${surface.boxShadow}, ${floatingShadow}`
      };
    }
    // Dark mode forced to a white tint (glassSurface's own default there is
    // `background.paper`, a dark neutral) — that reads fine on a big card,
    // where staying dark keeps it "raised" rather than disappearing, but a
    // small disc needs the opposite: dark-grey-over-dark-page barely
    // changes lightness at any opacity, so it read as barely-there instead
    // of the clearly defined circle the iOS Control Center reference this
    // is matching shows. White-over-dark is what actually differentiates
    // it.
    //
    // Dark weight dropped from an earlier 0.22 — reported live as too milky
    // across the whole face, "destroying the look of glass" (the Control
    // Center reference is a genuinely dark, mostly-transparent disc, not a
    // pale one). 0.12 keeps the disc readable as a distinct circle without
    // washing it out.
    const surface = glassSurface(theme, { light: 0.65, dark: 0.12 }, '#FFFFFF');
    // A faint hint of the product colour — reported live as reading "plain"
    // without it, a neutral grey/white disc with no tie to the product at
    // all. Deliberately much fainter than `glassTinted`'s own glow above
    // (that one means "selected"; this is just a whisper of brand colour on
    // an otherwise neutral disc, not a second "active" state).
    const productGlow = `0 0 10px ${alpha(theme.palette.primary.main, dark ? 0.18 : 0.12)}`;
    return {
      ...surface,
      boxShadow: `${surface.boxShadow}, ${productGlow}, ${floatingShadow}`
    };
  })();

  return {
    ...paint,
    // The sheen pseudo-element below is `position: absolute`, and the
    // glyph/initial the caller renders inside is plain `position: static`
    // — by CSS paint order, a positioned descendant with `z-index: auto`
    // paints ABOVE in-flow static content regardless of DOM order, so
    // without this the sheen was genuinely compositing on top of the icon,
    // not just tinting it (reported live in dark mode: "the white texture
    // hides the icon"). `isolation: isolate` scopes the stacking fix below
    // to just this element's own children, so it can't affect how this
    // chip stacks against anything else on the page.
    isolation: 'isolate',
    // A soft top-edge light sheen, so the chip reads as a curved glass
    // surface catching light rather than a flat tinted circle.
    //
    // Originally a broad wash down to 60% of the disc's height at up to
    // 0.35 alpha — reported live as "too much on the top center", reading
    // as a pale smear across most of the glyph's own area rather than a
    // highlight, and fighting the disc's visibility rather than selling
    // the glass. Pulled in to a thin rim crescent (stops at 28%) at a much
    // lower alpha, closer to the Control Center reference's own restraint:
    // a hint of light catching the top edge, not a wash over the center.
    //
    // `border-radius: inherit` on the pseudo-element itself clips the
    // gradient to the shape — the caller must NOT set `overflow: hidden`,
    // which would otherwise also clip the outer drop shadow above and
    // flatten the chip's lift off the page.
    '&::before': {
      content: '""',
      position: 'absolute',
      // Negative z-index (within the isolated stacking context above)
      // paints this BEHIND the icon/initial content instead of over it.
      zIndex: -1,
      inset: 0,
      borderRadius: 'inherit',
      background: `linear-gradient(180deg, ${alpha('#FFFFFF', dark ? 0.14 : 0.16)} 0%, transparent 28%)`,
      pointerEvents: 'none'
    }
  };
};

/**
 * The circular "icon button" disc — Figma "Icon Buttons" (50x50, r:999).
 *
 * Pulled out of `<Icon surface="…">` so any component that needs the exact
 * same disc around its own content (not necessarily an `<Icon>`) can reuse
 * it instead of re-deriving the recipe — see `FeatureCard/feature.tsx`'s
 * `surface` prop.
 */
function IconSurface({
  variant,
  tintColor,
  size = 50,
  onClick,
  children
}: TIconSurfaceProps) {
  const isGlass =
    variant === 'glass' ||
    variant === 'glassTinted' ||
    variant === 'glassStatus';
  const isInteractive = Boolean(onClick);

  return (
    <Box
      onClick={onClick}
      // A plain Box has no built-in keyboard affordance — without these,
      // an onClick glass chip (e.g. Icon's `surface="glass" onClick={…}`)
      // would be a mouse-only control. Only added when there's actually
      // something to activate.
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onKeyDown={
        isInteractive
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      sx={(theme) => ({
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        width: size,
        height: size,
        borderRadius: theme.radius.full,
        cursor: onClick ? 'pointer' : 'default',
        WebkitTapHighlightColor: 'transparent',
        ...(isGlass
          ? iconGlassChip(theme, variant, tintColor)
          : {
              // Figma paints both discs with Backgrounds/Primary and
              // nothing else — no backdrop blur, no rim, no inner
              // modelling. Only the "Round Button" carries a shadow.
              backgroundColor: theme.palette.background.paper,
              ...(variant === 'solid' && {
                boxShadow: theme.palette.shadows.icon
              })
            }),
        // The same press/hover/focus polish TopBar's glass hamburger and
        // avatar buttons use — kept here, not just duplicated there, so
        // any glass chip made interactive (`onClick`) reads as a real
        // button, not just a static disc that happens to catch a click.
        ...(isGlass &&
          isInteractive && {
            transition: 'transform 150ms ease, filter 150ms ease',
            '&:hover': { filter: 'brightness(1.06)' },
            '&:active': { transform: 'scale(0.94)' },
            '&:focus-visible': {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2
            }
          })
      })}
    >
      {children}
    </Box>
  );
}

export default IconSurface;
